import { Clock, Download, FileText } from "lucide-react"
import { Button } from "../ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../Card"

export default function TextBlock({ block, hideTitle = false, isConcatenated = false }) {
  const { content } = block || {}
  
  // Valeurs par défaut pour éviter les erreurs si les propriétés sont manquantes
  const readingTime = content?.readingTime ?? 0
  const wordCount = content?.wordCount ?? 0

  const formatReadingTime = (minutes) => {
    return `${minutes} min de lecture`
  }

  return (
    <div className="space-y-6">
      {/* CSS to impose app fonts on Wysiwyg content */}
      <style>{`
        .content-html {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .content-html h1,
        .content-html h2,
        .content-html h3,
        .content-html h4,
        .content-html h5,
        .content-html h6 {
          font-family: 'Fraunces', serif;
          font-weight: 400;
          line-height: 1.2;
        }
        .content-html p {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .content-html ul,
        .content-html ol {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .content-html li {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .content-html blockquote {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .content-html strong,
        .content-html b {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-weight: 600;
        }
      `}</style>
      {/* Reading Info */}
      {(readingTime > 0 || wordCount > 0) && !isConcatenated && (
        <Card>
          <CardContent className="p-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-4">
              {readingTime > 0 && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{formatReadingTime(readingTime)}</span>
                </div>
              )}
              {wordCount > 0 && (
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span>{wordCount} mots</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Main Content */}
      {isConcatenated ? (
        // Rendu fluide sans Card pour les blocs concaténés
        <div>
          {(() => {
            // Gérer les différents formats de content.html
            const rawHtml = content?.html 
              ? (typeof content.html === 'string' ? content.html : (content.html?.content || content.html || ''))
              : ''
            let sanitizedHtml = rawHtml || ''
            if (sanitizedHtml && typeof sanitizedHtml === 'string') {
              sanitizedHtml = sanitizedHtml.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
              const containsBlockTags = /<(p|ul|ol|h[1-6]|blockquote|pre|table)[\s>]/i.test(sanitizedHtml)
              if (!containsBlockTags) {
                // Transformer les doubles sauts de ligne en paragraphes, et les simples en <br/>
                const paragraphs = sanitizedHtml
                  .split(/\n{2,}/)
                  .map(p => `<p>${p.replace(/\n/g, '<br />')}</p>`)
                  .join('')
                sanitizedHtml = paragraphs
              }
            }
            return (
              <div 
                className="content-html text-lg leading-8"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            )
          })()}
        </div>
      ) : (
        <Card>
          {!hideTitle && (
            <CardHeader>
              <CardTitle>{block.title}</CardTitle>
              {block.description && (
                <p className="text-gray-600">{block.description}</p>
              )}
            </CardHeader>
          )}
          <CardContent className={hideTitle ? 'pt-6' : ''}>
            {(() => {
              // Gérer les différents formats de content.html
              const rawHtml = content?.html 
                ? (typeof content.html === 'string' ? content.html : (content.html?.content || content.html || ''))
                : ''
              let sanitizedHtml = rawHtml || ''
              if (sanitizedHtml && typeof sanitizedHtml === 'string') {
                sanitizedHtml = sanitizedHtml.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
                const containsBlockTags = /<(p|ul|ol|h[1-6]|blockquote|pre|table)[\s>]/i.test(sanitizedHtml)
                if (!containsBlockTags) {
                  // Transformer les doubles sauts de ligne en paragraphes, et les simples en <br/>
                  const paragraphs = sanitizedHtml
                    .split(/\n{2,}/)
                    .map(p => `<p>${p.replace(/\n/g, '<br />')}</p>`)
                    .join('')
                  sanitizedHtml = paragraphs
                }
              }
              return (
                <div 
                  className="content-html text-base leading-7"
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
              )
            })()}
          </CardContent>
        </Card>
      )}

      {/* Attachments */}
      {content?.attachments && content.attachments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Fichiers joints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {content.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">{attachment.name}</p>
                      <p className="text-sm text-gray-500">{attachment.type}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="inline-flex items-center justify-center gap-2 h-10 leading-none"
                    asChild
                  >
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 h-10 leading-none"
                    >
                      <Download className="w-4 h-4" />
                      <span>Télécharger</span>
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
